/**
 * RequestStatusBadge Component
 * Displays purchase request status with color coding and icon.
 *
 * Status IDs (from RequestStatus DB table):
 *  0 = Draft, 1 = Submitted, 2 = Accepted, 3 = Approved,
 *  4 = Order Pending, 5 = Order Generated, 6 = Invoice Pending,
 *  7 = Invoice Submitted, 8 = Invoice Paid, 9 = Closed,
 * -1 = On Hold, -2 = Rejected, -3 = Declined, -99 = Cancelled
 */

import { Badge, type ThemedBadgeProps } from '@/components/ui';
import {
  IconPencil,
  IconSend,
  IconCheck,
  IconChecks,
  IconShoppingCart,
  IconFileInvoice,
  IconCash,
  IconTruck,
  IconCircleCheck,
  IconClock,
  IconX,
  IconBan,
  IconCoin,
  IconTruckDelivery,
} from '@tabler/icons-react';

interface StatusInput {
  statusID: number;
  name?: string | null;
}

interface RequestStatusBadgeProps extends Omit<ThemedBadgeProps, 'children'> {
  status: StatusInput | number | undefined | null;
  showIcon?: boolean;
}

const STATUS_CONFIG: Record<
  number,
  { label: string; color: string; icon: React.ReactNode }
> = {
  '-3': {
    label: 'Cancelled',
    color: 'gray',
    icon: <IconBan size={12} />,
  },
  '-2': {
    label: 'Rejected',
    color: 'red',
    icon: <IconX size={12} />,
  },
  '-1': {
    label: 'On Hold',
    color: 'orange',
    icon: <IconClock size={12} />,
  },
  '0': {
    label: 'Draft',
    color: 'gray',
    icon: <IconPencil size={12} />,
  },
  '1': {
    label: 'Submitted',
    color: 'blue',
    icon: <IconSend size={12} />,
  },
  '2': {
    label: 'Line Approved',
    color: 'cyan',
    icon: <IconCheck size={12} />,
  },
  '3': {
    label: 'Finance Approved',
    color: 'green',
    icon: <IconChecks size={12} />,
  },
  '4': {
    label: 'Order Pending',
    color: 'violet',
    icon: <IconShoppingCart size={12} />,
  },
  '5': {
    label: 'Order Generated',
    color: 'violet',
    icon: <IconShoppingCart size={12} />,
  },
  '6': {
    label: 'Invoice Pending',
    color: 'orange',
    icon: <IconFileInvoice size={12} />,
  },
  '7': {
    label: 'Invoice Submitted',
    color: 'yellow',
    icon: <IconFileInvoice size={12} />,
  },
  '8': {
    label: 'Paid',
    color: 'teal',
    icon: <IconCash size={12} />,
  },
  '9': {
    label: 'Closed',
    color: 'green',
    icon: <IconCircleCheck size={12} />,
  },
  '10': {
    label: 'Completed',
    color: 'teal',
    icon: <IconCircleCheck size={12} />,
  },
};

export function RequestStatusBadge({
  status,
  showIcon = true,
  variant = 'light',
  size = 'sm',
  ...props
}: RequestStatusBadgeProps) {
  if (status === undefined || status === null) {
    return (
      <Badge variant={variant} size={size} color="gray" {...props}>
        Unknown
      </Badge>
    );
  }

  const statusID = typeof status === 'number' ? status : status.statusID;
  const statusName = typeof status === 'object' ? status.name : null;
  const config = STATUS_CONFIG[statusID];

  if (!config && !statusName) {
    return (
      <Badge variant={variant} size={size} color="gray" {...props}>
        Unknown
      </Badge>
    );
  }

  const label = statusName || config.label;
  const color = config?.color || 'gray';
  const icon = config?.icon;

  return (
    <Badge
      variant={variant}
      size={size}
      color={color}
      leftSection={showIcon && icon ? icon : undefined}
      {...props}
    >
      {label}
    </Badge>
  );
}
